/**
 * Theme Manager - Handles dark/light mode detection and management
 * @author Zubduq Team
 */

/**
 * Theme Manager class for handling automatic theme detection and theme switching
 */
class ThemeManager {
  constructor() {
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.currentTheme = 'light';
    this.callbacks = new Set();
    this.init();
  }

  /**
   * Initialize the theme manager
   */
  init() {
    try {
      // Listen for system theme changes
      this.mediaQuery.addEventListener('change', this.handleThemeChange.bind(this));
      
      // Load stored theme preference or fallback to system preference
      const storedTheme = this.getStoredThemePreference();
      if (storedTheme && (storedTheme === 'dark' || storedTheme === 'light')) {
        this.currentTheme = storedTheme;
        console.log(`🎨 Loading stored theme preference: ${storedTheme}`);
      } else {
        this.currentTheme = this.mediaQuery.matches ? 'dark' : 'light';
        console.log(`🎨 Using system theme preference: ${this.currentTheme}`);
      }
      
      this.applyTheme(this.currentTheme);
      
      // Add global accessibility enhancements
      this.enhanceAccessibility();
      this.applyAccessibilityEnhancements();
      
      // Initialize advanced features with default settings
      this.initAdvancedFeatures({
        addKeyboardShortcut: true,
        trackSystemChanges: true,
        autoSavePreference: true
      });
      
      console.log(`🎨 Theme Manager initialized - Current theme: ${this.currentTheme}`);
    } catch (error) {
      console.error('Error initializing Theme Manager:', error);
    }
  }

  /**
   * Handle system theme changes
   * @param {MediaQueryListEvent} e - Media query event
   */
  handleThemeChange(e) {
    // Only apply system theme changes if user hasn't set a manual preference
    const storedTheme = this.getStoredThemePreference();
    if (!storedTheme) {
      const newTheme = e.matches ? 'dark' : 'light';
      this.currentTheme = newTheme;
      this.applyTheme(newTheme);
      this.notifyCallbacks(newTheme);
      console.log(`🎨 System theme changed to: ${newTheme}`);
    } else {
      console.log(`🎨 System theme changed, but user has manual preference: ${storedTheme}`);
    }
  }

  /**
   * Apply theme to the document
   * @param {string} theme - Theme name ('dark' or 'light')
   */
  applyTheme(theme) {
    try {
      // Set data attribute for CSS targeting
      document.documentElement.setAttribute('data-theme', theme);
      
      // Update meta theme-color for mobile browsers
      this.updateMetaThemeColor(theme);
      
      // Update color scheme for form elements
      document.documentElement.style.colorScheme = theme;
      
      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('themechange', { 
        detail: { 
          theme,
          isDark: theme === 'dark',
          timestamp: Date.now()
        } 
      }));
      
      // Store preference for future sessions
      this.storeThemePreference(theme);
      
    } catch (error) {
      console.error('Error applying theme:', error);
    }
  }

  /**
   * Update meta theme-color for mobile browser UI
   * @param {string} theme - Theme name
   */
  updateMetaThemeColor(theme) {
    try {
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.name = 'theme-color';
        document.head.appendChild(themeColorMeta);
      }
      
      themeColorMeta.content = theme === 'dark' ? '#1a1a1a' : '#ffffff';
    } catch (error) {
      console.error('Error updating meta theme color:', error);
    }
  }

  /**
   * Store theme preference in localStorage
   * @param {string} theme - Theme name
   */
  storeThemePreference(theme) {
    try {
      localStorage.setItem('zubduq-theme-preference', theme);
    } catch (error) {
      console.warn('Could not store theme preference:', error);
    }
  }

  /**
   * Get stored theme preference
   * @returns {string|null} Stored theme preference or null
   */
  getStoredThemePreference() {
    try {
      return localStorage.getItem('zubduq-theme-preference');
    } catch (error) {
      console.warn('Could not retrieve theme preference:', error);
      return null;
    }
  }

  /**
   * Manually set theme (overrides system preference)
   * @param {string} theme - Theme name ('dark', 'light', or 'auto')
   */
  setTheme(theme) {
    try {
      if (theme === 'auto') {
        // Reset to system preference
        this.currentTheme = this.mediaQuery.matches ? 'dark' : 'light';
        localStorage.removeItem('zubduq-theme-preference');
      } else if (theme === 'dark' || theme === 'light') {
        this.currentTheme = theme;
        this.storeThemePreference(theme);
      } else {
        throw new Error(`Invalid theme: ${theme}`);
      }
      
      this.applyTheme(this.currentTheme);
      this.notifyCallbacks(this.currentTheme);
    } catch (error) {
      console.error('Error setting theme:', error);
    }
  }

  /**
   * Get current theme
   * @returns {string} Current theme name
   */
  getCurrentTheme() {
    return this.currentTheme;
  }

  /**
   * Check if current theme is dark mode
   * @returns {boolean} True if dark mode is active
   */
  isDarkMode() {
    return this.currentTheme === 'dark';
  }

  /**
   * Check if system prefers dark mode
   * @returns {boolean} True if system prefers dark mode
   */
  systemPrefersDark() {
    return this.mediaQuery.matches;
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme() {
    const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }

  /**
   * Add callback for theme changes
   * @param {Function} callback - Callback function to execute on theme change
   */
  addThemeChangeCallback(callback) {
    if (typeof callback === 'function') {
      this.callbacks.add(callback);
    }
  }

  /**
   * Remove theme change callback
   * @param {Function} callback - Callback function to remove
   */
  removeThemeChangeCallback(callback) {
    this.callbacks.delete(callback);
  }

  /**
   * Notify all registered callbacks of theme change
   * @param {string} theme - New theme name
   */
  notifyCallbacks(theme) {
    this.callbacks.forEach(callback => {
      try {
        callback(theme, this.isDarkMode());
      } catch (error) {
        console.error('Error in theme change callback:', error);
      }
    });
  }

  /**
   * Enhance accessibility for theme switching
   */
  enhanceAccessibility() {
    try {
      // Add reduced motion support
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        document.documentElement.style.setProperty('--transition-fast', '0s');
        document.documentElement.style.setProperty('--transition-base', '0s');
        document.documentElement.style.setProperty('--transition-slow', '0s');
      }
      
      // Add high contrast support detection
      if (window.matchMedia('(prefers-contrast: high)').matches) {
        document.documentElement.setAttribute('data-high-contrast', 'true');
      }
    } catch (error) {
      console.warn('Error enhancing accessibility:', error);
    }
  }

  /**
   * Get theme information for debugging
   * @returns {Object} Theme debug information
   */
  getDebugInfo() {
    return {
      currentTheme: this.currentTheme,
      systemPrefersDark: this.systemPrefersDark(),
      storedPreference: this.getStoredThemePreference(),
      callbacksCount: this.callbacks.size,
      mediaQuerySupported: !!this.mediaQuery,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Create a theme toggle button element
   * @param {Object} options - Button configuration options
   * @returns {HTMLElement} Theme toggle button element
   */
  createThemeToggleButton(options = {}) {
    const {
      className = 'theme-toggle-btn',
      lightIcon = '☀️',
      darkIcon = '🌙',
      autoIcon = '🔄',
      showLabel = true,
      position = null
    } = options;

    const button = document.createElement('button');
    button.className = className;
    button.setAttribute('aria-label', 'Toggle theme');
    button.setAttribute('role', 'button');
    button.setAttribute('type', 'button');

    // Add styles for positioning if specified
    if (position) {
      Object.assign(button.style, {
        position: 'fixed',
        zIndex: '1001',
        padding: '8px 12px',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--border-radius)',
        background: 'var(--color-surface)',
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        ...position
      });
    }

    // Update button content based on current theme
    const updateButton = () => {
      const isDark = this.isDarkMode();
      const icon = isDark ? lightIcon : darkIcon;
      const label = showLabel ? (isDark ? ' Light' : ' Dark') : '';
      button.innerHTML = `${icon}${label}`;
      button.title = `Switch to ${isDark ? 'light' : 'dark'} mode`;
    };

    // Initial update
    updateButton();

    // Add click handler
    button.addEventListener('click', () => {
      this.toggleTheme();
      updateButton();
    });

    // Listen for theme changes
    this.addThemeChangeCallback(updateButton);

    return button;
  }

  /**
   * Add theme transition animations
   * @param {number} duration - Animation duration in milliseconds
   */
  addThemeTransition(duration = 300) {
    try {
      const style = document.createElement('style');
      style.textContent = `
        *, *::before, *::after {
          transition: background-color ${duration}ms ease, 
                      color ${duration}ms ease, 
                      border-color ${duration}ms ease,
                      box-shadow ${duration}ms ease !important;
        }
      `;
      style.setAttribute('data-theme-transition', 'true');
      
      document.head.appendChild(style);
      
      // Remove after animation completes
      setTimeout(() => {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      }, duration + 50);
    } catch (error) {
      console.warn('Could not add theme transition:', error);
    }
  }

  /**
   * Add theme switching with smooth transition
   * @param {string} theme - Theme to switch to
   * @param {boolean} animate - Whether to animate the transition
   */
  setThemeWithTransition(theme, animate = true) {
    if (animate) {
      this.addThemeTransition();
    }
    this.setTheme(theme);
  }

  /**
   * Create a theme selector dropdown
   * @param {Object} options - Selector configuration options
   * @returns {HTMLElement} Theme selector element
   */
  createThemeSelector(options = {}) {
    const {
      className = 'theme-selector',
      includeAuto = true,
      showIcons = true
    } = options;

    const container = document.createElement('div');
    container.className = className;

    const select = document.createElement('select');
    select.setAttribute('aria-label', 'Choose theme');
    
    // Add options
    const themes = [
      { value: 'light', label: '☀️ Light', text: 'Light' },
      { value: 'dark', label: '🌙 Dark', text: 'Dark' }
    ];
    
    if (includeAuto) {
      themes.unshift({ value: 'auto', label: '🔄 Auto', text: 'Auto' });
    }

    themes.forEach(theme => {
      const option = document.createElement('option');
      option.value = theme.value;
      option.textContent = showIcons ? theme.label : theme.text;
      select.appendChild(option);
    });

    // Set current value
    const storedPreference = this.getStoredThemePreference();
    select.value = storedPreference || 'auto';

    // Add change handler
    select.addEventListener('change', (e) => {
      this.setThemeWithTransition(e.target.value, true);
    });

    container.appendChild(select);
    return container;
  }

  /**
   * Initialize advanced theme features
   * @param {Object} options - Initialization options
   */
  initAdvancedFeatures(options = {}) {
    const {
      createToggleButton = false,
      createSelector = false,
      addKeyboardShortcut = true,
      trackSystemChanges = true,
      autoSavePreference = true,
      buttonPosition = { top: '20px', right: '20px' }
    } = options;

    try {
      // Add keyboard shortcut (Ctrl/Cmd + Shift + T)
      if (addKeyboardShortcut) {
        document.addEventListener('keydown', (e) => {
          if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
            e.preventDefault();
            this.toggleTheme();
          }
        });
      }

      // Create toggle button
      if (createToggleButton) {
        const button = this.createThemeToggleButton({ position: buttonPosition });
        document.body.appendChild(button);
      }

      // Create theme selector
      if (createSelector) {
        const selector = this.createThemeSelector();
        // You can append this to a specific container
        window.themeSelector = selector; // Make available globally
      }

      // Enhanced system change tracking
      if (trackSystemChanges) {
        // Listen for high contrast preference changes
        const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
        highContrastQuery.addEventListener('change', (e) => {
          document.documentElement.setAttribute('data-high-contrast', e.matches);
        });

        // Listen for reduced motion preference changes
        const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        reducedMotionQuery.addEventListener('change', (e) => {
          if (e.matches) {
            document.documentElement.style.setProperty('--transition-fast', '0s');
            document.documentElement.style.setProperty('--transition-base', '0s');
            document.documentElement.style.setProperty('--transition-slow', '0s');
          } else {
            document.documentElement.style.removeProperty('--transition-fast');
            document.documentElement.style.removeProperty('--transition-base');
            document.documentElement.style.removeProperty('--transition-slow');
          }
        });
      }

      console.log('🎨 Advanced theme features initialized');
    } catch (error) {
      console.error('Error initializing advanced theme features:', error);
    }
  }

  /**
   * Get system accessibility preferences
   * @returns {Object} System accessibility settings
   */
  getSystemPreferences() {
    return {
      darkMode: this.systemPrefersDark(),
      highContrast: window.matchMedia('(prefers-contrast: high)').matches,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      forcedColors: window.matchMedia('(forced-colors: active)').matches
    };
  }

  /**
   * Apply accessibility enhancements based on system preferences
   */
  applyAccessibilityEnhancements() {
    const prefs = this.getSystemPreferences();
    
    if (prefs.highContrast) {
      document.documentElement.setAttribute('data-high-contrast', 'true');
    }
    
    if (prefs.reducedMotion) {
      document.documentElement.setAttribute('data-reduced-motion', 'true');
    }
    
    if (prefs.forcedColors) {
      document.documentElement.setAttribute('data-forced-colors', 'true');
    }
  }
}

// Create and export theme manager instance
const themeManager = new ThemeManager();

// Add to global scope for debugging and other components
window.themeManager = themeManager;

export default themeManager;
